import {CheckIcon, PlusCircledIcon} from "@radix-ui/react-icons";
import {Column} from "@tanstack/react-table";

import {cn} from "@/lib/utils";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {Separator} from "@/components/ui/separator";
import * as React from "react";
import {useTranslation} from "react-i18next";

interface DataTableFacetedFilterProps<TData, TValue> {
    column?: Column<TData, TValue>;
    title?: string;
    tableId?: string; //added to support split by "->" for specific (preference) table
}

/** Parses a facet key into one or more values to show in the filter, depending on table/column. */
function parseFacetKeyToValues(key: string, tableId: string | undefined, columnId: string | undefined): string[] {
    const s = String(key).trim();
    if (tableId === "preferences") {
        // Affinity groups are comma-separated, not arrow-separated
        if (columnId === "affinity_groups") {
            return s.split(",").map((v) => v.trim()).filter(Boolean);
        }
        return [s.split('->')[0].trim()];
    }
    if (tableId === "mentees") {
        return [s]; // commas are allowed in values
    }
    return s.split(',').map((v) => v.trim());
}

export function DataTableFacetedFilter<TData, TValue>({
                                                          column,
                                                          title,
                                                          tableId,
                                                      }: DataTableFacetedFilterProps<TData, TValue>) {
    const {t} = useTranslation();
    const facets = column?.getFacetedUniqueValues();

    const uniqueValueCounts = new Map<string, number>();
    facets?.forEach((count: number, key: unknown) => {
        const values = parseFacetKeyToValues(String(key), tableId, column?.id);
        values.forEach((value) => {
            const current = uniqueValueCounts.get(value) ?? 0;
            uniqueValueCounts.set(value, current + count);
        });
    });

    const options: { value: string; icon?: React.ComponentType<{ className?: string }>; }[] =
        Array.from(uniqueValueCounts.keys())
            .sort((a, b) => {
                if (a === "" && b === "") return 0;
                if (a === "") return -1;
                if (b === "") return 1;
                return a.localeCompare(b);
            })
            .map(value => ({ value, }));


    const selectedValues = new Set(column?.getFilterValue() as string[]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 border-dashed">
                    <PlusCircledIcon className="mr-2 h-4 w-4"/>
                    {t(`dataTable.columnHeader.headers.${title}`)}
                    {selectedValues?.size > 0 && (
                        <>
                            <Separator orientation="vertical" className="mx-2 h-4"/>
                            <Badge
                                variant="secondary"
                                className="rounded-sm px-1 font-normal lg:hidden"
                            >
                                {selectedValues.size}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                                {selectedValues.size > 2 ? (
                                    <Badge
                                        variant="secondary"
                                        className="rounded-sm px-1 font-normal"
                                    >
                                        {selectedValues.size} {t("dataTable.filter.selected")}
                                    </Badge>
                                ) : (
                                    options
                                        .filter((option) => selectedValues.has(option.value))
                                        .map((option) => (
                                            <Badge
                                                variant="secondary"
                                                key={option.value}
                                                className="rounded-sm px-1 font-normal capitalize"
                                            >
                                                {option.value}
                                            </Badge>
                                        ))
                                )}
                            </div>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align="start">
                <Command>
                    <CommandInput placeholder={t(`dataTable.columnHeader.headers.${title}`)}/>
                    <CommandList>
                        <CommandEmpty>{t("dataTable.noResults")}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = selectedValues.has(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => {
                                            if (isSelected) {
                                                selectedValues.delete(option.value);
                                            } else {
                                                selectedValues.add(option.value);
                                            }
                                            const filterValues = Array.from(selectedValues);
                                            column?.setFilterValue(
                                                filterValues.length ? filterValues : undefined,
                                            );
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                isSelected
                                                    ? "bg-primary text-primary-foreground"
                                                    : "opacity-50 [&_svg]:invisible",
                                            )}
                                        >
                                            <CheckIcon className={cn("h-4 w-4")}/>
                                        </div>
                                        {option.icon && (
                                            <option.icon className="mr-2 h-4 w-4 text-muted-foreground"/>
                                        )}
                                        <span className={`capitalize ${!option.value ? 'font-bold' : ''}`}>
                                            {option.value || t("dataTable.filter.notSpecified")}
                                        </span>
                                        {uniqueValueCounts?.get(option.value) && (
                                            <span
                                                className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                                                {uniqueValueCounts.get(option.value)}
                                            </span>
                                        )}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedValues.size > 0 && (
                            <>
                                <CommandSeparator/>
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() => column?.setFilterValue(undefined)}
                                        className="justify-center text-center"
                                    >
                                        {t("dataTable.filter.clearFilters")}
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
